import { axiosClient } from './axiosClient';
import { APIUrls } from '../constant/constant';

const { CONFIGURATION_ENDPOINTS } = APIUrls;

/**
 * Configuration API Module
 * 
 * This module handles all API calls related to device configuration management:
 * - File uploads for bulk device configuration
 * - Individual device CRUD operations
 * - Bulk device operations
 * - Device validation
 * 
 * Used by: src/pages/configuration/Configuration.js
 */

/**
 * Upload Excel file for bulk device configuration
 * @param {File} file - Excel file to upload
 * @param {string} uploadedBy - Email of the user uploading
 * @param {string} uploadType - Type of upload (e.g., 'bulk')
 * @returns {Promise<Object>} Upload response
 */
export async function uploadFileApi(file, uploadedBy, uploadType = 'bulk') {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('uploaded_by', uploadedBy);
    const response = await axiosClient.post(CONFIGURATION_ENDPOINTS.FILE_UPLOAD, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
}

/**
 * Create or update a device configuration
 * @param {Object} deviceData - Device configuration data
 * @param {string} updatedBy - Email of the user making changes
 * @param {string} action - Action type ('create' or 'update')
 * @returns {Promise<Object>} Save response
 */
export async function saveDeviceApi(deviceData, updatedBy, action = 'create') {
  try {
    const payload = {
      ...deviceData,
      updatedBy,
      action
    };

    const response = await axiosClient.post(CONFIGURATION_ENDPOINTS.DEVICES, payload);
    return response.data;
  } catch (error) {
    console.error('Error saving device:', error);
    throw error;
  }
}

/**
 * Get device configuration by device MAC
 * @param {string} deviceMac - Device MAC address to fetch
 * @returns {Promise<Object>} Device configuration
 */
export async function getDeviceConfigApi(deviceMac) {
  try {
    const response = await axiosClient.get(`${CONFIGURATION_ENDPOINTS.GET_DEVICE_DETAILS_BY_DEVICE_MAC}?device_mac=${deviceMac}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching device config for MAC ${deviceMac}:`, error);
    throw error;
  }
}

/**
 * Update existing device configuration
 * @param {string} deviceId - Device ID to update
 * @param {Object} deviceData - Updated device data
 * @param {string} updatedBy - Email of the user making changes
 * @returns {Promise<Object>} Update response
 */
export async function updateDeviceApi(deviceId, deviceData, updatedBy) {
  try {
    const payload = {
      ...deviceData,
      updatedBy,
      action: 'update'
    };

    const response = await axiosClient.put(`${CONFIGURATION_ENDPOINTS.DEVICES}/${deviceId}`, payload);
    return response.data;
  } catch (error) {
    console.error(`Error updating device ${deviceId}:`, error);
    throw error;
  }
}

/**
 * Delete device configuration
 * @param {string} deviceId - Device ID to delete
 * @param {string} deletedBy - Email of the user deleting
 * @returns {Promise<Object>} Delete response
 */
export async function deleteDeviceApi(deviceId, deletedBy) {
  try {
    const response = await axiosClient.delete(`${CONFIGURATION_ENDPOINTS.DEVICES}/${deviceId}`, {
      data: { deletedBy }
    });
    return response.data;
  } catch (error) {
    console.error(`Error deleting device ${deviceId}:`, error);
    throw error;
  }
}



/**
 * Get all device configurations
 * @returns {Promise<Object>} All device configurations
 */
export async function getAllDeviceConfigsApi() {
  try {
    const response = await axiosClient.get(CONFIGURATION_ENDPOINTS.DEVICES);
    return response.data;
  } catch (error) {
    console.error('Error fetching all device configs:', error);
    throw error;
  }
}

/**
 * Validate device configuration data
 * @param {Object} deviceData - Device data to validate
 * @returns {Promise<Object>} Validation response
 */
export async function validateDeviceApi(deviceData) {
  try {
    const response = await axiosClient.post(`${CONFIGURATION_ENDPOINTS.DEVICES}/validate`, deviceData);
    return response.data;
  } catch (error) {
    console.error('Error validating device:', error);
    throw error;
  }
}

/**
 * Get upload history for configuration devices
 * @returns {Promise<Array>} Array of upload history records
 */
export async function getUploadHistoryApi() {
  try {
    const response = await axiosClient.get(CONFIGURATION_ENDPOINTS.HISTORY);
    return response.data;
  } catch (error) {
    console.error('Error fetching upload history:', error);
    throw error;
  }
}

/**
 * Update device configuration using PUT API
 * @param {Object} deviceData - Device configuration data
 * @param {string} updatedBy - Email of the user making changes
 * @returns {Promise<Object>} Update response
 */
export async function updateDeviceConfigApi(deviceData, updatedBy) {
  try {
    const payload = {
      ...deviceData,
      changed_by: updatedBy
    };

    // If device_mac_changed_flag is false, remove both the flag and new_device_mac from payload
    if (!deviceData.device_mac_changed_flag) {
      delete payload.device_mac_changed_flag;
      delete payload.new_device_mac;
    }

    const response = await axiosClient.put(CONFIGURATION_ENDPOINTS.UPDATE_DEVICE, payload);
    return response.data;
  } catch (error) {
    console.error('Error updating device configuration:', error);
    throw error;
  }
}

/**
 * Get job status by job ID
 * @param {string} jobId - Job ID to check status for
 * @returns {Promise<Object>} Job status response
 */
export async function getJobStatusApi(jobId) {
  try {
    const response = await axiosClient.get(`${CONFIGURATION_ENDPOINTS.JOB_STATUS}/${jobId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching job status for ${jobId}:`, error);
    throw error;
  }
}
